import React from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Flex,
  Text,
  TextArea,
  TextField,
  useToast,
} from '@chatsdet/ui';
import { Plus, X } from '@phosphor-icons/react';
import {
  useCreateCredentialTemplate,
  useUpdateCredentialTemplate,
  type CredentialTemplate,
  type PersonaVariableDefinition,
} from '../../hooks/useCredentialTemplates';
import { toastErrorOptionsFor } from '../../lib/toast-error-options';
import { ApiError } from '../../lib/api-error';

type Props = {
  propertyId: string;
  propertyName: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** If provided, dialog is in edit mode */
  template?: CredentialTemplate | null;
  onSuccess?: (template: CredentialTemplate) => void;
};

export function CredentialTemplateDialog({
  propertyId,
  propertyName,
  isOpen,
  onOpenChange,
  template,
  onSuccess,
}: Props) {
  const { toast } = useToast();
  const [name, setName] = React.useState(template?.name ?? '');
  const [description, setDescription] = React.useState(template?.description ?? '');
  const [variableDefinitions, setVariableDefinitions] = React.useState<PersonaVariableDefinition[]>(
    template?.variableDefinitions ?? []
  );

  React.useEffect(() => {
    if (isOpen) {
      setName(template?.name ?? '');
      setDescription(template?.description ?? '');
      setVariableDefinitions(template?.variableDefinitions ?? []);
    }
  }, [isOpen, template]);

  const createMut = useCreateCredentialTemplate(propertyId);
  const updateMut = useUpdateCredentialTemplate(propertyId);
  const isEdit = !!template;
  const isPending = createMut.isPending || updateMut.isPending;

  const updateVariableDefinition = (index: number, patch: Partial<PersonaVariableDefinition>) => {
    setVariableDefinitions((defs) => defs.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  };

  const hasDuplicateVariableNames = React.useMemo(() => {
    const names = variableDefinitions.map((d) => d.name).filter(Boolean);
    return new Set(names).size !== names.length;
  }, [variableDefinitions]);

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Persona name is required');
      return;
    }
    if (hasDuplicateVariableNames) {
      toast.error('Variable names must be unique');
      return;
    }
    // Drop empty rows; server validates the name regex.
    const cleanedVars = variableDefinitions
      .map((d) => ({ ...d, name: d.name.trim() }))
      .filter((d) => d.name.length > 0);
    const payload = {
      name: trimmed,
      description: description.trim() || null,
      variableDefinitions: cleanedVars,
    };
    if (isEdit && template) {
      const runUpdate = (force: boolean) =>
        updateMut.mutate(
          { templateId: template.id, ...payload, force },
          {
            onSuccess: (t) => {
              toast.success(`Updated persona "${t.name}"`);
              onSuccess?.(t);
              onOpenChange(false);
            },
            onError: (err) => {
              // A removed variable still has filled values or workflow references.
              // Offer to force the removal rather than silently failing.
              if (
                !force &&
                err instanceof ApiError &&
                err.code === 'PERSONA_VARIABLE_IN_USE'
              ) {
                if (
                  window.confirm(
                    'One or more removed variables still have filled credential values or are referenced by workflows. Remove them anyway?'
                  )
                ) {
                  runUpdate(true);
                }
                return;
              }
              toast.error(
                err instanceof Error ? err.message : 'Update failed',
                toastErrorOptionsFor(err)
              );
            },
          }
        );
      runUpdate(false);
    } else {
      createMut.mutate(payload, {
        onSuccess: (t) => {
          toast.success(`Created persona "${t.name}"`);
          onSuccess?.(t);
          onOpenChange(false);
        },
        onError: (err) =>
          toast.error(
            err instanceof Error ? err.message : 'Create failed',
            toastErrorOptionsFor(err)
          ),
      });
    }
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      <span />
      <Dialog title={isEdit ? 'Edit Persona' : 'New Persona'}>
        <DialogContent>
          <Flex direction="column" gap="md">
            <Text size="sm" className="text-muted-foreground">
              Personas are logical credential identities on{' '}
              <Text as="span" weight="medium">
                {propertyName}
              </Text>
              . A persona groups one concrete credential per environment, so the same test can
              resolve the right credentials across staging, prod, sandbox, or local.
            </Text>
            <TextField
              label="Name"
              value={name}
              onChange={setName}
              isRequired
              placeholder="e.g., admin-user"
              description="Unique within this application."
              isDisabled={isPending}
            />
            <TextArea
              label="Description"
              value={description}
              onChange={setDescription}
              placeholder="What role does this credential represent?"
              isDisabled={isPending}
              rows={2}
              autoExpand
            />

            {/* Persona Variables — declared here, filled per-credential. */}
            <Flex direction="column" gap="sm">
              <Text size="sm" weight="medium">
                Variables
              </Text>
              <Text size="xs" className="text-muted-foreground">
                Declare variables this persona's credentials carry (e.g. ACCOUNT_ID). Fill each
                credential's values in its form; a provisioning sandbox action can also fill them.
                At run time they're injected as <code className="text-xs">$var.USER.&lt;NAME&gt;</code>.
              </Text>
              {variableDefinitions.length === 0 && (
                <Text size="sm" className="text-muted-foreground">
                  No variables declared yet.
                </Text>
              )}
              {variableDefinitions.map((def, index) => (
                <div
                  key={index}
                  className="space-y-2"
                  data-testid="persona-variable-definition-row"
                >
                  <Flex gap="sm" align="end">
                    <div className="flex-1">
                      <TextField
                        label="Name"
                        value={def.name}
                        onChange={(v) => updateVariableDefinition(index, { name: v })}
                        placeholder="ACCOUNT_ID"
                        isDisabled={isPending}
                      />
                    </div>
                    <div className="flex-[2]">
                      <TextField
                        label="Description"
                        value={def.description ?? ''}
                        onChange={(v) =>
                          updateVariableDefinition(index, { description: v || undefined })
                        }
                        placeholder="What this variable is for (optional)"
                        isDisabled={isPending}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove variable ${def.name || index + 1}`}
                      isDisabled={isPending}
                      onPress={() =>
                        setVariableDefinitions((defs) => defs.filter((_, i) => i !== index))
                      }
                      iconLeft={<X size={14} />}
                    />
                  </Flex>
                  <Checkbox
                    isSelected={def.sensitive ?? false}
                    onChange={(checked) =>
                      updateVariableDefinition(index, { sensitive: checked || undefined })
                    }
                    label="Sensitive (value is masked in the UI and redacted from agent context)"
                  />
                </div>
              ))}
              {hasDuplicateVariableNames && (
                <Text size="xs" className="text-destructive">
                  Variable names must be unique.
                </Text>
              )}
              <Flex>
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft={<Plus size={14} />}
                  isDisabled={isPending}
                  onPress={() =>
                    setVariableDefinitions((defs) => [...defs, { name: '' }])
                  }
                >
                  Add Variable
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={() => onOpenChange(false)} isDisabled={isPending}>
            Cancel
          </Button>
          <Button variant="primary" onPress={handleSubmit} isDisabled={isPending}>
            {isPending ? 'Saving…' : isEdit ? 'Save' : 'Create persona'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
